import React, { useEffect, useRef, useState } from 'react'
import { Modal, Form, Input, Button, message } from 'antd'
import { MobileOutlined, LockOutlined, SafetyCertificateOutlined, LeftOutlined } from '@ant-design/icons'
import { phonePasswordLogin, resetPassword, sendSmsCode } from '../../api'
import { setAuthSession } from '../../utils/auth'

const PHONE_RULES = [
    { required: true, message: '请输入手机号' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式' },
]

const getStoredPhone = () => {
    const phone = localStorage.getItem('phone') || ''
    if (/^1[3-9]\d{9}$/.test(phone)) return phone
    const username = localStorage.getItem('username') || ''
    return /^1[3-9]\d{9}$/.test(username) ? username : ''
}

const ChangePasswordModal = ({ open, onClose }) => {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [codeSending, setCodeSending] = useState(false)
    const [codeCountdown, setCodeCountdown] = useState(0)
    const timerRef = useRef(null)

    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current)
        }
    }, [])

    useEffect(() => {
        if (open) {
            form.resetFields()
            form.setFieldsValue({ phone: getStoredPhone() })
            setCodeCountdown(0)
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
        }
    }, [open, form])

    const startCountdown = () => {
        setCodeCountdown(60)
        timerRef.current = setInterval(() => {
            setCodeCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current)
                    timerRef.current = null
                    return 0
                }
                return prev - 1
            })
        }, 1000)
    }

    const handleSendCode = async () => {
        const phone = form.getFieldValue('phone')
        if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
            form.validateFields(['phone']).catch(() => {})
            message.warning('请输入正确的手机号')
            return
        }

        setCodeSending(true)
        try {
            const response = await sendSmsCode({ phone, target: 'update_pwd' })
            if (response.data && response.data.err_code === 200) {
                message.success('验证码已发送，请注意查收')
                startCountdown()
            } else {
                throw new Error(response.data?.err_msg || '验证码发送失败')
            }
        } catch (err) {
            message.error(err.message || '验证码发送失败，请重试')
        } finally {
            setCodeSending(false)
        }
    }

    const applyLoginSession = (data, fallbackPhone) => {
        const { token, user, is_admin } = data
        setAuthSession({
            token,
            username: user?.name || user?.phone || fallbackPhone,
            isAdmin: is_admin,
            userId: user?.id,
            phone: user?.phone || fallbackPhone,
        })
    }

    const handleSubmit = async (values) => {
        setLoading(true)
        try {
            const resetResp = await resetPassword({
                phone: values.phone,
                phone_code: values.code,
                password: values.password,
            })
            if (!(resetResp.data && resetResp.data.err_code === 200)) {
                throw new Error(resetResp.data?.err_msg || '密码修改失败')
            }

            const loginResp = await phonePasswordLogin({ phone: values.phone, password: values.password })
            if (loginResp.data && loginResp.data.err_code === 200 && loginResp.data.data && loginResp.data.data.token) {
                applyLoginSession(loginResp.data.data, values.phone)
                message.success('密码修改成功，已重新登录')
                onClose?.()
            } else {
                throw new Error(loginResp.data?.err_msg || '自动登录失败')
            }
        } catch (err) {
            message.error(err.message || '密码修改失败，请重试')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            centered
            width={420}
            title="修改密码"
            destroyOnClose
        >
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item name="phone" rules={PHONE_RULES}>
                    <Input
                        size="large"
                        prefix={<MobileOutlined />}
                        placeholder="请输入手机号"
                    />
                </Form.Item>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Form.Item
                        name="code"
                        rules={[{ required: true, message: '请输入验证码' }]}
                        style={{ flex: 1, marginBottom: 0 }}
                    >
                        <Input
                            size="large"
                            prefix={<SafetyCertificateOutlined />}
                            placeholder="请输入验证码"
                        />
                    </Form.Item>
                    <Button
                        size="large"
                        onClick={handleSendCode}
                        loading={codeSending}
                        disabled={codeCountdown > 0}
                    >
                        {codeCountdown > 0 ? `${codeCountdown}秒` : '获取验证码'}
                    </Button>
                </div>
                <Form.Item
                    name="password"
                    rules={[
                        { required: true, message: '请输入新密码' },
                        { min: 6, message: '密码至少6位' },
                    ]}
                    style={{ marginTop: 16 }}
                >
                    <Input.Password
                        size="large"
                        prefix={<LockOutlined />}
                        placeholder="请输入新密码（至少6位）"
                    />
                </Form.Item>
                <Form.Item
                    name="confirmPassword"
                    dependencies={["password"]}
                    rules={[
                        { required: true, message: '请确认新密码' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                    return Promise.resolve()
                                }
                                return Promise.reject(new Error('两次输入的密码不一致'))
                            },
                        }),
                    ]}
                >
                    <Input.Password
                        size="large"
                        prefix={<LockOutlined />}
                        placeholder="请再次输入新密码"
                    />
                </Form.Item>
                <Button type="primary" htmlType="submit" block size="large" loading={loading}>
                    确认修改并重新登录
                </Button>
            </Form>
        </Modal>
    )
}

export default ChangePasswordModal
